
import { FlashMode, FlashCommand, Block, CobberPattern } from "../Types";
import { commandToBinary } from "../Utils/Commands";
import { hasCobberSettings } from "../Lib/Devices";

class Pattern {
    modes: FlashMode[] = [];
    settings: any = {}; 
    blocks: Block[] = [];
    productId: number = 0;

    cobberAddresses: any[] = [
        {patternStart: 0x7000, setupStart: 0x6E09},
        {patternStart: 0x7080, setupStart: 0x6E26},
        {patternStart: 0x7100, setupStart: 0x6E43},
        {patternStart: 0x7180, setupStart: 0x6E60},
        {patternStart: 0x7200, setupStart: 0x6E7D},
        {patternStart: 0x7280, setupStart: 0x6E9A},
        {patternStart: 0x7300, setupStart: 0x6EB7},
        {patternStart: 0x7380, setupStart: 0x6ED4},
    ];

    constructor(modes: FlashMode[], productId: number, settings: any) {
        this.modes = modes;
        this.productId = productId;
        this.settings = settings;
    }

    getBlocks() {
        return hasCobberSettings(this.productId) ? this.getCobberBlocks() : this.getBlinderBlocks();
    }

    preProcess(pattern: FlashCommand[][]) {
        return JSON.parse(JSON.stringify(pattern)).map((pattern: FlashCommand[]) => {
            const newPattern: FlashCommand[] = [];
            pattern.forEach((command: FlashCommand) => {
                if(command.command === 'channelSelect') {
                    if(Array.isArray(command.data) ) {
                        // Always set all channels
                        let channels = command.data.filter((data: number) => data <= 4);
                        newPattern.push({
                            command: 'channelSelect',
                            data: channels.length ? channels : [7]
                        })
                        channels = command.data.filter((data: number) => data > 4);
                        command.data = channels.length ? channels : [8];
                    }
                }

                newPattern.push(command);

            });
            return newPattern;
        })
    }

    getCobberBlocks() {
        // Create blocks
        const blocks = [
            // Block to set the global settings
            this.getBlock(
                0x6E00, 
                [
                    this.settings.accel_threshold,
                    this.settings?.min_time_before_sleep ?? 0,
                    this.settings?.min_time_before_wake ?? 0,
                    this.modes.reduce((acc: number, mode: FlashMode) => {
                        if (mode.id !== 0) acc++
                        return acc;
                    }, 0),
                    this.settings?.n_interrupts ?? 0,
                    this.settings.sleep_threshold,
                    this.settings?.wake_interrupt_duration ?? 0,
                    this.settings?.sleep_interrupt_duration ?? 0,
                ]
            )
        ];

        for (let i = 0; i < this.cobberAddresses.length; i++) {
            if(this.modes[i].id) {
                const pattern: any = this.modes[i].config?.eyeSaver ? this.modes[i].eye_saver_pattern : this.modes[i].flash_pattern;
                const x_patterns = this.preProcess(pattern.x_patterns ?? []);
                const z_patterns = this.preProcess(pattern.z_patterns ?? []);

                if(x_patterns.length !== 0) {
                    z_patterns.unshift([])
                }

                const patternOffsets = [0];

                x_patterns.forEach((x: any) => {
                    patternOffsets.push(x.length + patternOffsets[patternOffsets.length - 1])
                });

                z_patterns.forEach((z: any) => {
                    patternOffsets.push(z.length + patternOffsets[patternOffsets.length - 1])
                });

                blocks.push(
                    this.getBlock(
                        this.cobberAddresses[i]['setupStart'], 
                        [
                            ...this.getSetup('x', this.modes[i]),
                            x_patterns.length,
                            ...this.getSetup('z', this.modes[i]),
                            z_patterns.length,
                            ...patternOffsets
                        ]
                    )
                )

                blocks.push(
                    this.getBlock(
                        this.cobberAddresses[i]['patternStart'], 
                        [
                            ...(x_patterns.flatMap((pattern: any) => {
                                    return pattern.map((command: FlashCommand) => {
                                        return parseInt(commandToBinary(command, this.modes[i]) ?? '', 2);
                                    })
                                }
                            )),
                            ...(z_patterns.flatMap((pattern: any) => {
                                return pattern.map((command: FlashCommand) => {
                                    return parseInt(commandToBinary(command, this.modes[i]) ?? '', 2);
                                })
                            }
                        )),
                        ]
                    )
                )
            }
        }
        
        return blocks;
    }

    getBlinderBlocks() {
        const startAddress = 63488;

        const modePositionData = [
            this.modes.some((mode: FlashMode) => mode.id > 0) ? 1 : 0
        ];

        let index = 1;
        for (let i = 0; i < 9; i++) {
            modePositionData.push(index);
            if(this.modes[i]) index += this.modes[i].flash_pattern.length;
        }

        const data = this.modes.map((mode: FlashMode) => 
            mode.flash_pattern.map((command: FlashCommand) => {
                return parseInt(commandToBinary(command, mode) ?? '', 2);
            }
        )).reduce((a, b) => a.concat(b), [])
        
        // Create blocks
        return [
            // Block to set the mode indexes
            this.getBlock(startAddress, modePositionData),
            // Block to set the patterns
            this.getBlock(startAddress + 64, data),
        ];
    }

    getBlock(startAddress: number, data: any) {
        return {   
            "block": true, 
            "start_address": startAddress, 
            "end_address": startAddress + data.length, 
            "length": data.length, 
            "data": data
        }
    }

    getSetup(type: string, mode: FlashMode): number[] {
        const setup: any = (mode.flash_pattern as CobberPattern)?.setup;
        if(!setup) return [0,0,0,0];

        const typeSetup = setup[type];
        if(!typeSetup) return [0,0,0,0];

        const typeSetting: any = {
            'x': mode?.config?.tiltSensitivity,
            'z': mode?.config?.brakeSensitivity
        }

        const adjustedSetup: number[] = [typeSetup.shortFactor, typeSetup.longFactor];

        const adjustedInt = Math.min(
            (typeSetting[type] ? typeSetting[type] : 1) * typeSetup.threshold, 
            65535
        );

        adjustedSetup.push(parseInt(adjustedInt.toString(16).padStart(4, '0').slice(0, 2), 16));
        adjustedSetup.push(parseInt(adjustedInt.toString(16).padStart(4, '0').slice(2), 16));

        return adjustedSetup;
    }
}

export default Pattern;